import { useTranslation } from 'react-i18next';

export default function Pricing() {
  const { t } = useTranslation();

  const plans = [
    {
      name: t('pricing.basic.name'),
      price: 79,
      period: t('pricing.basic.period'),
      description: t('pricing.basic.description'),
      features: [
        t('pricing.basic.feature1'),
        t('pricing.basic.feature2'),
        t('pricing.basic.feature3'),
        t('pricing.basic.feature4'),
      ],
      popular: false,
      pricePerMeal: 15.80
    },
    {
      name: t('pricing.pro.name'),
      price: 139,
      period: t('pricing.pro.period'),
      description: t('pricing.pro.description'),
      features: [
        t('pricing.pro.feature1'),
        t('pricing.pro.feature2'),
        t('pricing.pro.feature3'),
        t('pricing.pro.feature4'),
        t('pricing.pro.feature5'),
      ],
      popular: true,
      pricePerMeal: 13.90
    },
    {
      name: t('pricing.elite.name'),
      price: 189,
      period: t('pricing.elite.period'),
      description: t('pricing.elite.description'),
      features: [
        t('pricing.elite.feature1'),
        t('pricing.elite.feature2'),
        t('pricing.elite.feature3'),
        t('pricing.elite.feature4'),
        t('pricing.elite.feature5'),
        t('pricing.elite.feature6'),
      ],
      popular: false,
      pricePerMeal: 12.60
    },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('pricing.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-2xl p-8 ${
                plan.popular
                  ? 'bg-teal-50 border-2 border-teal-500 shadow-xl scale-105'
                  : 'bg-white border border-gray-200 shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {t('pricing.popular')}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600">/{plan.period}</span>
                </div>
                <p className="text-sm text-gray-500">${plan.pricePerMeal} per meal</p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={scrollToContact}
                className={`w-full py-3 px-6 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {t('pricing.getStarted')}
              </button>
            </div>
          ))}
        </div>

        {/* Additional Pricing Info */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">What's Included in Every Plan</h3>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Fresh Ingredients</h4>
              <p className="text-sm text-gray-600">Locally sourced, organic when possible</p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-recycle-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Eco-Friendly</h4>
              <p className="text-sm text-gray-600">Sustainable packaging and practices</p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Flexible Delivery</h4>
              <p className="text-sm text-gray-600">Choose your delivery days and times</p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Quality Guarantee</h4>
              <p className="text-sm text-gray-600">100% satisfaction or money back</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}